import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { FindDocumentById } from 'src/graphql/documents';
import { DocumentType } from 'src/utils/enum';
import CustomModal from '../configuracion/components/customModal';
import LoadingSpinner from 'src/components/general/js/spinnerLoading';
import TaxDocumentDetail from './documentTypes/TaxDocumentDetail';
import FeesDetail from './documentTypes/FeesDetail';
import CreditsDetail from './documentTypes/CreditsDetail';
import InvoicesDetail from './documentTypes/InvoicesDetail';
import ObligationsDetail from './documentTypes/ObligationsDetail';
import DinDetail from './documentTypes/DinDetail';
import { useDocumentDetailState, closeDocumentDetail } from 'src/utils/documentModal';
import moment from 'moment';
import { formatMoneyWithCurrency, formatUtcCalendarDate } from 'src/utils/formatter';
import { getObligationTypeDisplayLabel } from 'src/components/general/js/forms/reconcile/components/matchRowHelpers';
import { getObligationDisplayAmount } from 'src/components/tarjetasCredito/tcObligationHelpers';
import { getDocumentTypeDisplayLabel } from 'src/utils/documentsI18n';

const DocumentDetailContainer = () => {
  const { t } = useTranslation('documents');
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  
  const location = useLocation();
  const history = useHistory();
  
  // Use the utility hook to get modal state
  const { isVisible, documentId, companyId } = useDocumentDetailState(location);
  // FindDocumentById expects an array, so wrap documentId in array if it exists.
  // Sin companyId, el exchange usa la empresa seleccionada.
  const { data: result, fetching, reexecuteQuery } = FindDocumentById({ 
    ids: documentId ? [documentId] : [],
    ...(companyId ? { companyId } : {}),
  });

  // Force refresh when modal opens - remove reexecuteQuery from dependencies to prevent infinite loops
  useEffect(() => {
    if (isVisible && documentId) {
      setIsLoading(true);
      setRefreshKey(prev => prev + 1);
      // Only refetch if we have a valid documentId
      if (documentId) {
        reexecuteQuery({ requestPolicy: 'network-only' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, documentId]); // Removed reexecuteQuery from dependencies

  // Handle loading state
  useEffect(() => {
    if (result && !fetching) {
      setIsLoading(false);
    }
  }, [result, fetching, refreshKey]);

  // Close modal and clean URL (restaura ruta previa si se abrió desde Pascale)
  const closeModal = () => {
    closeDocumentDetail(history, location);
    setIsLoading(true);
  };

  // Get document data
  const document = result?.findDocumentsById?.[0];

  // Determine document type and render appropriate component
  const renderDocumentDetail = () => {
    if (!document) return null;

    const documentType = document.documentType
    const isBoletaHonorarios = [0, 1, '0', '1'].includes(document.meta?.documentCode)
    // const isDinDocument = [914, '914'].includes(document.meta?.documentCode)
    
    // Use document ID as key to force re-mount when document changes
    const documentKey = document.id || document._id;

    if (isBoletaHonorarios) return <FeesDetail key={documentKey} data={document} />
    // if (isDinDocument) return <DinDetail key={documentKey} data={document} />
    
    const onDocumentUpdated = () => reexecuteQuery({ requestPolicy: 'network-only' });

    switch (documentType) {
      case DocumentType.TAX_DOCUMENT:
        return (
          <TaxDocumentDetail
            key={documentKey}
            data={document}
            onDocumentUpdated={onDocumentUpdated}
          />
        );
      case DocumentType.CREDIT:
        return <CreditsDetail key={documentKey} data={document} />;
      case DocumentType.INVOICES:
        return <InvoicesDetail key={documentKey} data={document} />;
      case DocumentType.OBLIGATION:
        return <ObligationsDetail key={documentKey} data={document} />;
      default:
        return (
          <TaxDocumentDetail
            key={documentKey}
            data={document}
            onDocumentUpdated={onDocumentUpdated}
          />
        );
    }
  };

  // Generate modal title (siempre mostrar label legible para obligaciones, no el type crudo)
  const getModalTitle = () => {
    if (!document) return t('creditDetail.documentDetailFallback');
    const docSummary = document.documentType === DocumentType.OBLIGATION
      ? getObligationTypeDisplayLabel(document.meta)
      : (getDocumentTypeDisplayLabel(document)
      || (document.documentType === DocumentType.CREDIT ? t('creditDetail.creditInstallment') : t('creditDetail.documentFallback')));
    const docNumber = document.meta?.documentId || document.meta?.number || '';
    const docNumberDisplay = docNumber ? ` #${docNumber}` : '';
    const amount =
      document.documentType === DocumentType.OBLIGATION
        ? getObligationDisplayAmount(document)
        : document.meta?.originalAmount ||
          document.meta?.amount ||
          document.amount ||
          document.meta?.netAmount ||
          0;
    const formattedAmount = formatMoneyWithCurrency(amount, document.currencyType || 'CLP');
    const docDate =
      document.documentType === DocumentType.OBLIGATION
        ? formatUtcCalendarDate(document.documentDate) ?? ''
        : document.documentDate
          ? moment(document.documentDate).format('DD/MM/YYYY')
          : '';

    return t('detail.modalTitle', {
      summary: docSummary,
      number: docNumberDisplay,
      amount: formattedAmount,
      date: docDate,
    });
  };

  // Don't render modal if not visible
  if (!isVisible) {
    return null;
  }

  return (
    <CustomModal
      key={`document-detail-${documentId || 'no-id'}-${refreshKey}-${isVisible}`}
      open={isVisible}
      onClose={closeModal}
      title={getModalTitle()}
      classNameContent="document-detail-modal-content"
      content={
        <div className="tw-p-4">
          {isLoading ? (
            <div className="tw-flex tw-justify-center tw-items-center tw-h-32">
              <LoadingSpinner
                height="25px"
                afterHeight="25px"
                afterWidth="25px"
              />
            </div>
          ) : !document ? (
            <div className="tw-flex tw-justify-center tw-items-center tw-h-32">
              <span className="tw-text-gray-500">{t('detail.documentNotFound')}</span>
            </div>
          ) : (
            renderDocumentDetail()
          )}
        </div>
      }
      size="large"
      classNameTitle="!tw-text-center !tw-text-[14px] !tw-font-semibold"
      cancelarLabel={t('configuracion:common.close')}
      onCancel={closeModal}
      dimmer={{ style: { zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' } }}
      modalStyle={{
        width: '800px',
        maxWidth: '80%',
        minWidth: '800px',
        maxHeight: '100vh',
        overflow: 'hidden',
        margin: '0 auto',
      }}
    />
  );
};

export default DocumentDetailContainer; 